import { computed, toValue } from 'vue';
import { RouteRecordRaw } from 'vue-router';

import OnboardingPersonalLayout from '@/modules/onboarding-personal-profile/components/onboarding-personal-layout.vue';
import PersonalProfileKyc from '@/modules/onboarding-personal-profile/pages/personal-profile-kyc/personal-profile-kyc.vue';
import { RoutePermission } from '@/router/route-permission';
import { usePersonalOnboardingStore } from '@/stores/OnboardingPersonal';

const PersonalProfileWallets = () => import('@/modules/onboarding-user-profile/pages/user-profile-wallets/UserProfileWallets.vue');

const AccountStatus = () => import('@/modules/onboarding-user-profile/pages/user-account-status/user-account-status.vue');
const PersonalProfileTransactions = () => import('@/modules/transactions-old/pages/TransactionsPage.vue');
const PersonalProfileLoginAndSecurity = () => import('@/modules/onboarding-user-profile/pages/user-login-and-security/user-login-and-security.vue');
const UserPlan = () => import('@/modules/onboarding-user-profile/pages/user-plan/UserPlan.vue');
const PersonalProfileSubscriptions = () => import('@/modules/user-profile/pages/user-subscriptions/index.vue');
const AccountNavigation = () => import('@/shared-components/account-nav.vue');

const ONBOARDING_PERSONAL_PROFILE_META = {
  groupName: 'onboarding-personal-profile',
  layout: {
    props: {
      backName: [
        'onboarding-users',
      ],
    },
  },
};

export const ONBOARDING_PERSONAL_ACCOUNT_TAB_CHILDREN: RouteRecordRaw[] = [
  {
    path: 'security',
    name: 'onboarding-personal-account-login-and-security',
    component: PersonalProfileLoginAndSecurity,
    props: () => ({
      userDetails: usePersonalOnboardingStore().userDetails,
      updateUserContacts: usePersonalOnboardingStore().updateUserContacts,
      applicationType: 'personal',
      disabled: usePersonalOnboardingStore().applicationStatus === 'CLOSED',
    }),
    meta: {
      translation: 'pages.onboarding.admin_page.security',
      isHide: computed(() => usePersonalOnboardingStore().isPapiWithoutUserDetails),
      ...ONBOARDING_PERSONAL_PROFILE_META,
    },
  },
  {
    path: 'status',
    name: 'onboarding-personal-account-status',
    component: AccountStatus,
    props: () => ({
      applicationStatus: usePersonalOnboardingStore().applicationStatus,
      blocked: usePersonalOnboardingStore().blocked,
      toggleBlockStatus: usePersonalOnboardingStore().toggleBlockStatus,
      banned: usePersonalOnboardingStore().banned,
      unban: usePersonalOnboardingStore().unban,
      close: usePersonalOnboardingStore().close,
    }),
    meta: {
      translation: 'pages.onboarding.admin_page.account_blocking',
      ...ONBOARDING_PERSONAL_PROFILE_META,
    },
  },
];

export const ONBOARDING_PERSONAL_PROFILE_CHILDREN: RouteRecordRaw[] = [
  {
    path: 'profile',
    name: 'onboarding-personal-details',
    component: PersonalProfileKyc,
    meta: {
      translation: 'pages.onboarding.admin_page.profile',
      icon: 'icon-user-onboarding',
      ...ONBOARDING_PERSONAL_PROFILE_META,
    },
  },
  {
    path: 'wallets',
    name: 'onboarding-personal-wallets',
    component: PersonalProfileWallets,
    props: () => ({
      organizationId: usePersonalOnboardingStore().userDetails?.organizationId,
    }),
    meta: {
      translation: 'pages.user_profile.navigation.wallets',
      icon: 'icon-wallet',
      isHide: computed(() => usePersonalOnboardingStore().isPapiWithoutUserDetails),
      ...ONBOARDING_PERSONAL_PROFILE_META,
    },
  },
  {
    path: 'transactions',
    name: 'onboarding-personal-transactions',
    component: PersonalProfileTransactions,
    props: () => ({
      defaultFilter: {
        orgIds: [usePersonalOnboardingStore().userDetails?.organizationId],
      },
      isTitleVisible: false,
      isFullPage: false,
      isAddCharge: usePersonalOnboardingStore().applicationStatus === 'VERIFIED',
    }),
    meta: {
      translation: 'pages.user_profile.navigation.transactions',
      icon: 'icon-list',
      isHide: computed(() => usePersonalOnboardingStore().isPapiWithoutUserDetails),
      ...ONBOARDING_PERSONAL_PROFILE_META,
    },
  },
  {
    path: 'plan',
    name: 'onboarding-personal-plan',
    component: UserPlan,
    props: () => ({
      userId: usePersonalOnboardingStore().userDetails?.userId,
      role: usePersonalOnboardingStore().userDetails?.role,
      customerType: 'PERSONAL',
    }),
    meta: {
      translation: 'pages.onboarding.admin_page.plan',
      icon: 'icon-coins',
      isHide: computed(() => usePersonalOnboardingStore().isPapiWithoutUserDetails),
      ...ONBOARDING_PERSONAL_PROFILE_META,
    },
  },
  {
    path: 'subscriptions',
    name: 'onboarding-personal-subscriptions',
    component: PersonalProfileSubscriptions,
    props: () => ({
      organizationId: usePersonalOnboardingStore().userDetails?.organizationId,
      disabled: usePersonalOnboardingStore().applicationStatus === 'CLOSED',
    }),
    meta: {
      translation: 'pages.contracts.navigation.subscriptions',
      icon: 'icon-card-thin',
      isHide: computed(() => usePersonalOnboardingStore().isPapiWithoutUserDetails),
      ...ONBOARDING_PERSONAL_PROFILE_META,
    },
  },
  {
    path: 'account',
    name: 'onboarding-personal-account',
    component: AccountNavigation,
    props: () => ({
      userId: usePersonalOnboardingStore().userDetails?.userId,
      tabs: ONBOARDING_PERSONAL_ACCOUNT_TAB_CHILDREN,
    }),
    children: ONBOARDING_PERSONAL_ACCOUNT_TAB_CHILDREN,
    redirect: () => {
      const childToRedirect = ONBOARDING_PERSONAL_ACCOUNT_TAB_CHILDREN.find((child) => toValue(child.meta?.isHide) !== true);
      if (!childToRedirect) return ONBOARDING_PERSONAL_ACCOUNT_TAB_CHILDREN[0];
      return childToRedirect;
    },
    meta: {
      translation: 'pages.onboarding.admin_page.account',
      icon: 'icon-lock',
      ...ONBOARDING_PERSONAL_PROFILE_META,
    },
  },
];

export const ONBOARDING_PERSONAL: RouteRecordRaw = {
  path: '/dashboard/customers/personal/:id',
  name: ONBOARDING_PERSONAL_PROFILE_META.groupName,
  children: ONBOARDING_PERSONAL_PROFILE_CHILDREN,
  component: OnboardingPersonalLayout,
  redirect: (to) => ({
    name: ONBOARDING_PERSONAL_PROFILE_CHILDREN[0].name,
    params: to.params,
  }),
  meta: {
    permission: RoutePermission.ONBOARDING_USERS,
    root: false,
    ...ONBOARDING_PERSONAL_PROFILE_META,
  },
};
